import { InputAdornment, SxProps, TextField } from "@mui/material";
import classNames from "classnames";
import { useValidationBlur } from "../../hooks/useValidationBlur";

export const HdgTextInput = <T = string,>({
  label,
  name,
  value = "" as T,
  onChange,
  onSubmit,
  className,
  type = "text",
  multiline = false,
  isValid = true,
  helperText,
  disabled = false,
  startDecorator,
  endDecorator,
  sx,
  size = "medium",
  validationText,
  minRows,
  placeholder,
  inputProps,
}: {
  label?: string;
  name?: string;
  value?: T;
  onChange: (value: T) => void;
  onSubmit?: (value: T) => void;
  className?: string;
  type?: React.HTMLInputTypeAttribute;
  multiline?: boolean;
  isValid?: boolean;
  helperText?: string;
  disabled?: boolean;
  startDecorator?: React.ReactNode;
  endDecorator?: React.ReactNode;
  sx?: SxProps;
  size?: "small" | "medium";
  validationText?: string;
  minRows?: number;
  placeholder?: string;
  inputProps?: React.ComponentProps<typeof TextField>["inputProps"];
}) => {
  const { shouldValidate, onBlur } = useValidationBlur();

  const adornments = {
    startAdornment: null,
    endAdornment: null,
  } as {
    startAdornment: React.ReactNode | null;
    endAdornment: React.ReactNode | null;
  };

  if (startDecorator) {
    adornments.startAdornment = (
      <InputAdornment position="start">{startDecorator}</InputAdornment>
    );
  }

  if (endDecorator) {
    adornments.endAdornment = (
      <InputAdornment position="end">{endDecorator}</InputAdornment>
    );
  }

  return (
    <TextField
      size={size}
      fullWidth
      variant="outlined"
      type={type}
      label={label}
      name={name}
      value={value}
      onChange={(e) => onChange(e.target.value as T)}
      onKeyDown={(e) => {
        if (e.key === "Enter" && onSubmit) {
          e.preventDefault();
          onSubmit(value as T);
        }
      }}
      className={classNames("hdg-text-input", className)}
      multiline={multiline}
      minRows={multiline ? minRows ?? 2 : 1}
      helperText={
        validationText && shouldValidate ? validationText : helperText
      }
      error={shouldValidate && !isValid}
      disabled={disabled}
      placeholder={placeholder}
      inputProps={inputProps}
      slotProps={{
        input: adornments,
      }}
      onBlur={onBlur}
      sx={{
        marginBottom: '16px',
        ...sx
      }}
    />
  );
};
